import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';

import { isLowQualityPhoto } from '../lib/findings';
import { t } from '../lib/i18n';
import type { Cents, ReceiptSession } from '../lib/types';
import { useAppStore } from '../store';

interface BatchProgressProps {
  sessions: readonly ReceiptSession[];
}

/**
 * Sum every successful receipt's total, grouped by currency. Low-quality-
 * photo sessions are excluded — their parsed totals can't be trusted, and
 * silently rolling them into the grand total would let bad numbers
 * inflate the figure the user sees. Mirrors the same exclusion in
 * `lib/export-xlsx.ts` and `lib/export-zip.ts`.
 *
 * Cross-currency sums aren't meaningful so each currency gets its own
 * entry. Insertion order preserved so the dominant currency in a typical
 * single-currency batch shows up first.
 */
function summariseTotals(
  sessions: readonly ReceiptSession[],
): Array<{ currency: string; total: Cents }> {
  const buckets = new Map<string, Cents>();
  for (const s of sessions) {
    if (s.state.kind !== 'success' || s.receipt === null) continue;
    if (isLowQualityPhoto(s)) continue;
    const ccy = s.receipt.currency.value;
    buckets.set(ccy, (buckets.get(ccy) ?? 0) + s.receipt.total.value);
  }
  return Array.from(buckets, ([currency, total]) => ({ currency, total }));
}

function formatCents(c: Cents): string {
  return (c / 100).toFixed(2);
}

/**
 * Aggregate progress across all sessions in the queue.
 * Each terminal session (success / error) counts as 1.0, each currently
 * processing one contributes its fractional progress, queued ones contribute
 * 0. Result is fraction completed across the whole batch.
 *
 * Renders nothing when there are no sessions — the dropzone empty state
 * already conveys "nothing yet".
 */
export function BatchProgress({ sessions }: BatchProgressProps) {
  const lang = useAppStore((s) => s.lang);
  if (sessions.length === 0) return null;

  let completed = 0;
  let processingProgress = 0;
  let activeProcessing = false;
  let errors = 0;
  let queued = 0;
  let reviews = 0;

  for (const s of sessions) {
    switch (s.state.kind) {
      case 'success':
        completed += 1;
        break;
      case 'error':
        completed += 1;
        errors += 1;
        break;
      case 'processing':
        activeProcessing = true;
        // SUM, not assign — with a multi-worker pool several sessions
        // process concurrently and each contributes its own fraction.
        processingProgress += s.state.progress ?? 0;
        break;
      case 'queued':
        queued += 1; // contributes 0 progress
        break;
      case 'review':
        // Crop-review gate: parked on the user, nothing in flight.
        reviews += 1;
        break;
    }
  }

  // Everything left is waiting on the user's crop approval — an animated
  // "in progress" bar would be lying. The review card itself is the CTA.
  if (!activeProcessing && queued === 0 && reviews > 0) return null;

  const total = sessions.length;
  const fraction = Math.min(1, (completed + processingProgress) / total);
  const percent = Math.round(fraction * 100);
  const allDone = completed === total;

  // Status icon + bar tint follow the same logic — green when fully done
  // with no errors, amber when done with errors, indigo while in flight.
  let Icon: typeof CheckCircle2;
  let iconClass: string;
  let fillClass: string;
  if (allDone) {
    if (errors > 0) {
      Icon = AlertCircle;
      iconClass = 'text-amber-600 dark:text-amber-400';
      fillClass = 'bg-amber-500';
    } else {
      Icon = CheckCircle2;
      iconClass = 'text-emerald-600 dark:text-emerald-400';
      fillClass = 'bg-gradient-to-r from-emerald-500 to-emerald-600';
    }
  } else {
    Icon = Loader2;
    iconClass = 'animate-spin text-indigo-600 dark:text-indigo-400';
    fillClass = 'bg-gradient-to-r from-indigo-500 to-indigo-600';
  }

  let label: string;
  if (allDone) {
    if (errors > 0) {
      label = t(
        lang,
        total === 1
          ? 'batch.allDoneWithErrors.one'
          : 'batch.allDoneWithErrors.many',
        { total, errors, s: errors === 1 ? '' : 's' },
      );
    } else {
      label = t(
        lang,
        total === 1 ? 'batch.allDone.one' : 'batch.allDone.many',
        { total },
      );
    }
  } else if (activeProcessing) {
    label = t(lang, 'batch.processing', { done: completed, total });
  } else {
    label = t(lang, 'batch.partial', { done: completed, total });
  }

  // Grand total per currency, only when the whole batch is done — mirrors
  // the Grand Total row in the xlsx/zip exports so the user sees the same
  // figure in the UI as in their downloaded workbook.
  const totals = allDone ? summariseTotals(sessions) : [];

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="mb-2.5 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-sm">
        <span className="flex items-center gap-2 font-medium text-slate-700 dark:text-slate-200">
          <Icon className={`h-4 w-4 ${iconClass}`} aria-hidden="true" />
          {label}
        </span>
        <span className="text-xs font-semibold tabular-nums text-slate-500 dark:text-slate-400">
          {percent}%
        </span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
        <div
          className={`h-full ${fillClass} transition-[width] duration-300 ease-out`}
          style={{ width: `${percent}%` }}
          role="progressbar"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={label}
        />
      </div>
      {totals.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-slate-100 pt-3 text-sm dark:border-slate-800">
          <span className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
            {t(lang, 'batch.grandTotal')}
          </span>
          {totals.map(({ currency, total }) => (
            <span
              key={currency}
              className="font-semibold tabular-nums text-slate-800 dark:text-slate-100"
            >
              {formatCents(total)}{' '}
              <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
                {currency}
              </span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
